import { Button } from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONCEPT_DOMAIN,
  CONCEPT_NOTE_AUTOMATIC_THOUGHT_DETAIL_ENDPOINT,
  CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
  conceptApi,
} from "api/conceptApi";
import React, { useCallback, useState } from "react";
import { useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="11"
    height="8"
    viewBox="0 0 11 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1 3.5L4 6.5L9.5 1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function AddConceptNoteButton({
  thoughtRecordKey,
}: {
  thoughtRecordKey: string;
}) {
  const queryClient = useQueryClient();
  const patientState = useRecoilValue(patientAtom);
  const [disabled, setDisabled] = useState<boolean>(false);

  const getAutomaticThoughtDetailQueryFn = useCallback(
    () =>
      conceptApi.noteAutomaticThoughtDetail(
        `${patientState?.patientId}`,
        thoughtRecordKey
      ),
    [patientState?.patientId, thoughtRecordKey]
  );

  const { refetch } = useQuery(
    [
      CONCEPT_DOMAIN,
      CONCEPT_NOTE_AUTOMATIC_THOUGHT_DETAIL_ENDPOINT,
      patientState?.patientId,
      thoughtRecordKey,
    ],
    getAutomaticThoughtDetailQueryFn,
    {
      enabled: !!patientState?.patientId && !!thoughtRecordKey,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          setDisabled(data.result !== undefined);
        }
      },
    }
  );

  const addAutomaticThoughtQueryFn = useCallback(
    () =>
      conceptApi.addAutomaticThought(
        `${patientState?.patientId}`,
        `${thoughtRecordKey}`
      ),
    [patientState?.patientId, thoughtRecordKey]
  );

  const { mutate: addAutomaticThought } = useMutation(
    addAutomaticThoughtQueryFn,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_AUTOMATIC_THOUGHT_LIST_ENDPOINT,
          patientState?.patientId,
        ]);

        queryClient.invalidateQueries([
          CONCEPT_DOMAIN,
          CONCEPT_NOTE_AUTOMATIC_THOUGHT_DETAIL_ENDPOINT,
          patientState?.patientId,
          thoughtRecordKey,
        ]);
      },
    }
  );

  return (
    <Button
      size="sm"
      disabled={disabled}
      variant={disabled ? "Outlined" : "Solid"}
      sx={{
        pointerEvents: disabled  ? "none" : "auto",
      }}
      startDecorator={
        disabled ? <CheckIcon /> : undefined
      }
      onClick={() => {
        addAutomaticThought();
      }}
    >
      {disabled ? "추가 됨" : "어시스턴트에 추가하기"}
    </Button>
  );
}